'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BlockingReason, IncidentStatus } from '@evimo/types';
import { IncidentService } from '@/app/services/incident/incident.service';

export const INCIDENT_BLOCKING_REASONS: BlockingReason[] = [
  'WAITING_PARTS',
  'WAITING_CLIENT',
  'WAITING_DSO',
  'WAITING_CPO_BACKEND',
  'WAITING_SITE_ACCESS',
  'WAITING_SUPERVISOR_APPROVAL',
  'WAITING_TELEMETRY_WINDOW',
  'WAITING_VENDOR_RMA',
];

type PendingTransition = {
  incidentId: string;
  nextStatus: IncidentStatus;
};

const DEFAULT_BLOCKING_REASON: BlockingReason = 'WAITING_PARTS';

export function useIncidentStatusTransition() {
  const { t } = useTranslation('incidents');
  const queryClient = useQueryClient();
  const [pendingTransition, setPendingTransition] =
    useState<PendingTransition | null>(null);
  const [blockingReason, setBlockingReason] = useState<BlockingReason>(
    DEFAULT_BLOCKING_REASON,
  );
  const [transitioningIncidentId, setTransitioningIncidentId] = useState<
    string | null
  >(null);

  const transitionMutation = useMutation({
    mutationFn: async (params: {
      incidentId: string;
      nextStatus: IncidentStatus;
      blockingReason?: BlockingReason | null;
    }) => {
      setTransitioningIncidentId(params.incidentId);

      return IncidentService.transitionIncidentStatus(params);
    },
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['incidents'] });
      message.success(
        t('hook.transition.success', {
          status: t(`status.${variables.nextStatus}`, variables.nextStatus),
          defaultValue: 'Incident moved to {{status}}.',
        }),
      );
      setPendingTransition(null);
      setBlockingReason(DEFAULT_BLOCKING_REASON);
    },
    onError: (mutationError: Error) => {
      message.error(
        mutationError.message ||
          t('hook.transition.failed', 'Failed to update the incident status.'),
      );
    },
    onSettled: () => {
      setTransitioningIncidentId(null);
    },
  });

  const requestTransition = (
    incidentId: string,
    nextStatus: IncidentStatus,
  ) => {
    if (nextStatus === 'BLOCKED') {
      setPendingTransition({ incidentId, nextStatus });
      setBlockingReason(DEFAULT_BLOCKING_REASON);
      return;
    }

    transitionMutation.mutate({
      incidentId,
      nextStatus,
      blockingReason: null,
    });
  };

  const confirmBlockedTransition = async (): Promise<void> => {
    if (!pendingTransition) {
      return;
    }

    await transitionMutation.mutateAsync({
      incidentId: pendingTransition.incidentId,
      nextStatus: pendingTransition.nextStatus,
      blockingReason,
    });
  };

  const cancelPendingTransition = () => {
    if (transitionMutation.isPending) {
      return;
    }

    setPendingTransition(null);
    setBlockingReason(DEFAULT_BLOCKING_REASON);
  };

  return {
    blockingReason,
    cancelPendingTransition,
    confirmBlockedTransition,
    isTransitionDialogOpen: pendingTransition !== null,
    isTransitioning: transitionMutation.isPending,
    pendingTransition,
    requestTransition,
    setBlockingReason,
    transitioningIncidentId,
  };
}
